import './PendingStatus.css';

/**
 * PendingStatus — a visually-hidden polite live region that announces a
 * control's async pending state to screen readers.
 *
 * WHY (Codex #463 audit, F-a): the Spinner `decorative` sweep correctly
 * stopped in-button spinners from folding "Loading" into their control's
 * accessible name — but at sites with no surrounding live region it removed
 * the ONLY pending cue a screen-reader user got. A disabled button whose text
 * flips to "Saving…" is not a reliable announcement: name changes on a
 * disabled/just-clicked control are not spoken by most AT, and a focused
 * button that then unmounts drops focus to <body>. This restores exactly one
 * dependable cue.
 *
 * HOW: render it as a SIBLING of the control (never a child — a live region
 * inside a <button> would re-fold into the accessible name, recreating the
 * double-announce the sweep fixed). It is ALWAYS mounted; only its text
 * content changes when `pending` flips. That ordering matters: a live region
 * inserted into the DOM already populated is announced inconsistently across
 * AT, whereas a pre-existing region whose CONTENT changes is announced
 * reliably. Since the control's form/panel is on screen before the user can
 * trigger the action, the empty region always pre-exists at trigger time.
 *
 * WHEN NOT TO USE: omit it where an ancestor already owns a
 * role="status"/aria-live for this exact state (e.g. GrowthView's `gx-notice`,
 * ProjectPanel's `iv-grading` poll region) — two live regions double-announce.
 *
 * @param {boolean} pending  whether the async action is in flight
 * @param {React.ReactNode} children  the message to announce while pending
 *   (mirror the button's visible pending text, e.g. "Saving…")
 */
export default function PendingStatus({ pending, children }) {
  return (
    <span className="pending-status" role="status">
      {pending ? children : ''}
    </span>
  );
}
